import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircle, X, Send, Bot, User, Sparkles } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'

type Message = { role: 'user' | 'assistant'; content: string }

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-study`

// Pages where the floating AI button should be hidden (marketing/footer pages)
const HIDDEN_ON_ROUTES = ['/', '/how-it-works', '/features', '/pricing', '/blog', '/privacy', '/terms', '/refund-policy', '/contact', '/category']

const categorySuggestions: Record<string, string[]> = {
  'Nail Technician': ['What topics are on the nail tech exam?', 'Explain sanitation protocols', 'Tips for passing my state exam'],
  Electrician: ['Explain NEC code basics', 'What are grounding requirements?', 'Tips for the electrician exam'],
  Plumber: ['Explain drainage & venting', 'What plumbing codes should I know?', 'Tips for the plumber exam'],
  Cosmetology: ['Key topics on the cosmetology exam?', 'Explain chemical service safety', 'Tips for passing my state exam'],
  HVAC: ['Explain refrigeration cycles', 'What EPA regulations should I know?', 'Tips for the HVAC exam'],
}

export function AIStudyChat() {
  const { user } = useAuth()
  const location = useLocation()
  const shouldHide = HIDDEN_ON_ROUTES.some(r => r === '/' ? location.pathname === '/' : location.pathname.startsWith(r))
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [userCategory, setUserCategory] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!user) return
    supabase.from('profiles').select('category').eq('user_id', user.id).single()
      .then(({ data }) => { if (data?.category) setUserCategory(data.category) })
  }, [user])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus()
  }, [isOpen])

  const lastSentAt = useRef(0)

  const send = async () => {
    const trimmed = input.trim()
    if (!trimmed || isLoading) return
    // Rate limit: 1 message per 2 seconds
    if (Date.now() - lastSentAt.current < 2000) return
    // Input length guard
    if (trimmed.length > 2000) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Your message is too long. Please keep it under 2000 characters.' }])
      return
    }
    // Conversation length guard
    if (messages.length >= 40) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Conversation limit reached. Please close and reopen the chat to start a new session.' }])
      return
    }
    lastSentAt.current = Date.now()
    const userMsg: Message = { role: 'user', content: trimmed }
    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setIsLoading(true)

    let assistantSoFar = ''

    try {
      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ messages: [...messages, userMsg], mode: 'study-chat', category: userCategory }),
      })

      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({ error: 'Failed to connect' }))
        setMessages((prev) => [...prev, { role: 'assistant', content: err.error || 'Something went wrong. Please try again.' }])
        setIsLoading(false)
        return
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let textBuffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        textBuffer += decoder.decode(value, { stream: true })

        let newlineIndex: number
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex)
          textBuffer = textBuffer.slice(newlineIndex + 1)
          if (line.endsWith('\r')) line = line.slice(0, -1)
          if (!line.startsWith('data: ')) continue
          const jsonStr = line.slice(6).trim()
          if (jsonStr === '[DONE]') break
          try {
            const parsed = JSON.parse(jsonStr)
            const content = parsed.choices?.[0]?.delta?.content
            if (content) {
              assistantSoFar += content
              const final = assistantSoFar
              setMessages((prev) => {
                const last = prev[prev.length - 1]
                if (last?.role === 'assistant') {
                  return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: final } : m))
                }
                return [...prev, { role: 'assistant', content: final }]
              })
            }
          } catch {
            textBuffer = line + '\n' + textBuffer
            break
          }
        }
      }
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }])
    }

    setIsLoading(false)
  }

  if (shouldHide) return null

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-xl flex items-center justify-center hover:bg-primary active:bg-primary transition-colors"
          >
            <Sparkles className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5" />
                <div>
                  <p className="font-semibold text-sm">AI Study Assistant</p>
                  <p className="text-[10px] opacity-80">Powered by Certelyx AI</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <Sparkles className="w-8 h-8 text-primary mx-auto mb-3" />
                  <p className="font-semibold text-foreground text-sm">How can I help you study?</p>
                  <p className="text-xs text-muted-foreground mt-1">Ask me anything about your {userCategory || 'trade'} exam</p>
                  <div className="mt-4 space-y-2">
                    {(categorySuggestions[userCategory || ''] || categorySuggestions['Nail Technician']).map((q) => (
                      <button
                        key={q}
                        onClick={() => { setInput(q); }}
                        className="block w-full text-left text-xs bg-secondary hover:bg-secondary/80 rounded-lg px-3 py-2 text-foreground transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Bot className="w-3.5 h-3.5 text-primary" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-foreground'
                    }`}
                  >
                    {msg.content}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 mt-0.5">
                      <User className="w-3.5 h-3.5 text-primary-foreground" />
                    </div>
                  )}
                </div>
              ))}

              {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                <div className="flex gap-2">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Bot className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="bg-secondary rounded-xl px-3 py-2">
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-border p-3 shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && send()}
                  placeholder="Ask a study question..."
                  className="flex-1 bg-secondary rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button
                  onClick={send}
                  disabled={!input.trim() || isLoading}
                  className="bg-primary text-primary-foreground p-2.5 rounded-lg hover:bg-primary active:bg-primary transition-colors disabled:opacity-100"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
